import React, { useState, useEffect } from 'react';
import { View, Text, ImageBackground, TextInput, TouchableOpacity, Image, Pressable, ScrollView, Platform, Modal } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getFirestore, collection, query, where, getDocs, limit, doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Constants from 'expo-constants';
import { auth } from '../config/firebase';
import { globalStyles } from '../styles/globalStyles';
import { getResponsiveValue } from '../styles/breakpoints';
import { useUser } from '../components/LoginPersistence';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { AuthSelect } from '../components/AuthSelect';
import { useTypingAnimation } from '../hooks/useTypingAnimation';
import { useRoute } from '@react-navigation/native';
import { validateInviteUrl, useInviteUrl, getInviteCodeFromUrl } from '../utils/inviteUrlUtils';

// Debug configuration
console.log('Development configuration in Login:', {
  devAdminEmail: Constants.expoConfig.extra.devAdminEmail,
  devUserEmail: Constants.expoConfig.extra.devUserEmail,
  devPassword: Constants.expoConfig.extra.devPassword ? '[EXISTS]' : '[MISSING]'
});

const DEFAULT_PROFILE_PHOTOS = [
  'https://firebasestorage.googleapis.com/v0/b/venue-report-card.firebasestorage.app/o/userProfilePhotos%2Fkoi.png?alt=media&token=ee0cda3e-781a-4d2d-9bbe-8df851ede3fa',
  'https://firebasestorage.googleapis.com/v0/b/venue-report-card.firebasestorage.app/o/userProfilePhotos%2Fpanda-bear.png?alt=media&token=ee0cda3e-781a-4d2d-9bbe-8df851ede3fa',
  'https://firebasestorage.googleapis.com/v0/b/venue-report-card.firebasestorage.app/o/userProfilePhotos%2Fostrich.png?alt=media&token=ee0cda3e-781a-4d2d-9bbe-8df851ede3fa',
  'https://firebasestorage.googleapis.com/v0/b/venue-report-card.firebasestorage.app/o/userProfilePhotos%2Fshark.png?alt=media&token=ee0cda3e-781a-4d2d-9bbe-8df851ede3fa',
  'https://firebasestorage.googleapis.com/v0/b/venue-report-card.firebasestorage.app/o/userProfilePhotos%2Fbear.png?alt=media&token=ee0cda3e-781a-4d2d-9bbe-8df851ede3fa'
];


const AuthInput = ({ label, value, onChangeText, onKeyPress, placeholder, secureTextEntry, showPlaceholder = false, color = '#e9e9e7', onSubmitEditing }) => {
  const [isFocused, setIsFocused] = useState(false);

  // Check if this input should be highlighted
  // const isHighlighted = ['CONFIRM PASSWORD', 'USERNAME', 'YOUR ROLE', 'FIRST NAME', 'LAST NAME'].includes(label.toUpperCase());

  return (
    <View onClick={() => {
      if (Platform.OS === 'web') {
        document.querySelector(`input[placeholder="${placeholder}"]`).focus();
      }
    }} style={globalStyles.inputRow}>
      <Text 
        style={[globalStyles.authInputLabel, 
        { 
          fontSize: label.length > 12 
            ? getResponsiveValue({
              mobile: 16,
              tablet: 18,
              laptop: 20,
              desktop: 20,
            })
            : getResponsiveValue({
              mobile: 20,
              tablet: 22,
              laptop: 24,
              desktop: 24,
            })
          }, 
          { 
            color: isFocused ? color : color
          }]}
      >
        {label}
      </Text>
      <TextInput 
        onBlur={() => setIsFocused(false)} 
        onFocus={() => setIsFocused(true)} 
        id={label} 
        style={[globalStyles.authInput, { 
          borderBottomColor: isFocused ? color : color,
          color: isFocused ? color : color
        }]} 
        value={value} 
        onChangeText={onChangeText} 
        onKeyPress={onKeyPress}
        onSubmitEditing={onSubmitEditing}
        placeholder={placeholder} 
        placeholderTextColor={showPlaceholder && !isFocused ? color : 'transparent'} 
        secureTextEntry={secureTextEntry} 
        returnKeyType={secureTextEntry || label.toLowerCase().includes('password') ? 'done' : 'next'}
      />
    </View>
  )
}

const getRandomDefaultPhoto = () => {
  return DEFAULT_PROFILE_PHOTOS[Math.floor(Math.random() * DEFAULT_PROFILE_PHOTOS.length)];
};

// Password validation rules (only length constraints)
const PASSWORD_REQUIREMENTS = {
  minLength: 7, // longer than 6
  maxLength: 20, // shorter than 20
};

const validatePassword = (password) => {
  const errors = [];
  
  if (password.length < PASSWORD_REQUIREMENTS.minLength) {
    errors.push(`Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters long`);
  }
  if (password.length >= PASSWORD_REQUIREMENTS.maxLength) {
    errors.push(`Password must be less than ${PASSWORD_REQUIREMENTS.maxLength} characters`);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Common offensive terms list (this is a basic example, you'd want a more comprehensive list in production)
const OFFENSIVE_TERMS = [
  'offensive', 'slur', 'inappropriate', 'profanity',
  // Add more terms as needed
];

const validateUsername = async (username) => {
  const errors = [];
  
  // Basic validation (length only)
  if (username.length <= 6) {
    errors.push('Username must be at least 6 characters long');
    return { isValid: false, errors };
  }
  if (username.length >= 20) {
    errors.push('Username must be less than 20 characters');
    return { isValid: false, errors };
  }

  // Check for offensive terms
  const lowercaseUsername = username.toLowerCase();
  for (const term of OFFENSIVE_TERMS) {
    if (lowercaseUsername.includes(term)) {
      errors.push('Username contains inappropriate content');
      return { isValid: false, errors };
    }
  }

  // Check if username is already taken
  try {
    const db = getFirestore();
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('username', '==', username), limit(1));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      errors.push('Username is already taken');
      return { isValid: false, errors };
    }
  } catch (error) {
    console.error('Error checking username availability:', error);
    errors.push('Error checking username availability');
    return { isValid: false, errors };
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

const ROLES = [
  'Live Performer',
  'Venue Employee',
  'Touring Crew',
  'Backline Tech',
  'Booking Agent',
  'Promoter',
  'Venue Manager',
  'Tour Manager',
  'LD',
  'FOH',
];

const ACCOUNT_TYPES = [
  { label: 'Individual', value: 'individual' },
  { label: 'Business', value: 'business' },
  { label: 'Band', value: 'band' },
  { label: 'Organization', value: 'organization' }
];

export function LoginScreen({ navigation, route: routeProp }) {
  const { setUser } = useUser();
  const routeHook = useRoute();
  const route = routeProp || routeHook;
  const auth = getAuth();
  
  // Check for invite parameter - if present, automatically set to register mode
  const checkForInvite = () => {
    // Check route params first
    if (route?.params?.invite) return true;
    // Check URL search params for web
    if (typeof window !== 'undefined' && window.location) {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('invite') !== null;
    }
    return false;
  };
  
  const [isLogin, setIsLogin] = useState(!route?.params?.isSignUp && !checkForInvite());
  const [formData, setFormData] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    userType: '',
    accountType: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const animatedRolePlaceholder = useTypingAnimation(ROLES, 100, 50, 2000);
  
  // Extract returnTo from route params if available
  const returnTo = route.params?.returnTo || 'Home';
  
  useEffect(() => {
    // Set message from route params if available
    if (route?.params?.message) {
      setMessage(route.params.message);
    }
    
    // If invite parameter is present, switch to register mode
    const hasInvite = checkForInvite();
    if (hasInvite && isLogin) {
      setIsLogin(false);
    }
  }, [route?.params]);

  useEffect(() => {
    const autoLogin = async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);
        if (process.env.NODE_ENV === 'development') {
          const email = Constants.expoConfig.extra.devAdminEmail;
          const password = Constants.expoConfig.extra.devPassword;

          console.log('Attempting auto-login with:', { email, password: password ? '[EXISTS]' : '[MISSING]' });
          
          if (!email || !password) {
            console.error('Missing development credentials');
            return;
          }

          const userCredential = await signInWithEmailAndPassword(auth, email, password);
          const db = getFirestore();
          const usersRef = collection(db, 'users');
          const q = query(usersRef, where('email', '==', userCredential.user.email.toLowerCase()), limit(1));
          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            const userData = querySnapshot.docs[0].data();
            setUser({ ...userData, uid: userCredential.user.uid });
            navigation.replace(returnTo || 'Home');
          }
        } else {
          const currentUser = auth.currentUser;
          if (currentUser) {
            const db = getFirestore();
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('email', '==', currentUser.email.toLowerCase()), limit(1));
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
              const userData = querySnapshot.docs[0].data();
              setUser({ ...userData, uid: currentUser.uid });
              navigation.replace(returnTo || 'Home');
            }
          }
        }
      } catch (error) {
        console.error('Auto-login error:', error);
      }
    };

    // autoLogin();
  }, [auth, navigation, setUser, returnTo]);

  const handleLogin = async () => {
    if (!formData.email || !formData.password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
      const db = getFirestore();
      const usersRef = collection(db, 'users');
      const q = query(
        usersRef,
        where('email', '==', userCredential.user.email.toLowerCase()),
        limit(1)
      );
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0].data();
        setUser({ ...userData, uid: userCredential.user.uid });
        
        // Allow admins to bypass suspension check
        if (userData.userType === 'admin') {
          navigation.replace(returnTo || 'Home');
        } else if (userData.status === 'suspended') {
          navigation.replace('Waitlist');
        } else {
          navigation.replace(returnTo || 'Home');
        }
      } else {
        // Create a basic user document if it doesn't exist
        const userData = {
          email: userCredential.user.email.toLowerCase(),
          userType: 'user',
          status: 'suspended',
          createdAt: new Date(),
          submissions: 0,
          isPublic: false,
          registrationMessage: 'Welcome to Venue Report Card! We are currently in closed alpha. Your account has been added to our waitlist and will be activated soon.',
        };
        
        await setDoc(doc(db, 'users', userCredential.user.uid), userData);
        setUser({ ...userData, uid: userCredential.user.uid });
        navigation.replace('Waitlist');
      }
    } catch (err) {
      console.error('Login error:', err);
      let errorMessage = 'Invalid email or password';
      
      if (err.code === 'auth/invalid-email') errorMessage = 'Invalid email format';
      else if (err.code === 'auth/user-not-found') errorMessage = 'No account found with this email';
      else if (err.code === 'auth/wrong-password') errorMessage = 'Incorrect password';
      else if (err.message === 'Unauthorized access') errorMessage = 'Access denied. Admin privileges required.';
      else if (err.message === 'User data not found') errorMessage = 'User profile not found. Please contact support.';
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    setError('');
    setLoading(true);

    try {
      // Validate all required fields
      if (!formData.username || !formData.firstName || !formData.lastName || 
          !formData.email || !formData.password || !formData.confirmPassword || !formData.userType) {
        setError('Please fill in all fields');
        setLoading(false);
        return;
      }

      // Password validation
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }

      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        setError(passwordValidation.errors.join('\n'));
        setLoading(false);
        return;
      }

      // Username validation
      const usernameValidation = await validateUsername(formData.username);
      if (!usernameValidation.isValid) {
        setError(usernameValidation.errors.join('\n'));
        setLoading(false);
        return;
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('Please enter a valid email address');
        setLoading(false);
        return;
      }

      // Name validation
      if (!/^[a-zA-Z\s-']+$/.test(formData.firstName) || !/^[a-zA-Z\s-']+$/.test(formData.lastName)) {
        setError('Names can only contain letters, spaces, hyphens, and apostrophes');
        setLoading(false);
        return;
      }

      // Check for invite URL from URL parameters
      let inviteUrlId = null;
      let isCommunityMember = false;
      const inviteCodeFromUrl = getInviteCodeFromUrl(route?.params || {});
      
      if (inviteCodeFromUrl) {
        // Validate invite URL
        const urlValidation = await validateInviteUrl(inviteCodeFromUrl);
        if (!urlValidation.valid) {
          setError(urlValidation.error || 'Invalid invite link');
          setLoading(false);
          return;
        }
        inviteUrlId = urlValidation.urlId;
        isCommunityMember = true;
      }

      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const displayName = `${formData.firstName} ${formData.lastName}`;
      const photoURL = getRandomDefaultPhoto();
      
      await updateProfile(userCredential.user, { displayName, photoURL });

      const db = getFirestore();
      const userData = {
        username: formData.username,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email.toLowerCase(),
        photoURL: photoURL,
        folders: {},
        files: {},
        status: isCommunityMember ? 'active' : 'suspended',
        userType: formData.userType.toLowerCase(),
        createdAt: new Date(),
        submissions: 0,
        accountType: formData.accountType,
        isPublic: false,
        isCommunityMember: isCommunityMember,
        registrationMessage: isCommunityMember 
          ? 'Welcome to Venue Report Card! Your account has been activated with community member access.'
          : 'Welcome to Venue Report Card! We are currently in closed alpha. Your account has been added to our waitlist and will be activated soon.',
      };

      await setDoc(doc(db, 'users', userCredential.user.uid), userData);

      // Record invite URL usage now that user is authenticated
      if (inviteUrlId) {
        const urlUsageResult = await useInviteUrl(inviteUrlId, userCredential.user.uid);
        if (!urlUsageResult.success) {
          console.error('Failed to record invite URL usage:', urlUsageResult.error);
          // Don't block registration if recording fails, but log it
        }
      }
      
      try {
        const functions = getFunctions();
        const handleNewRegistration = httpsCallable(functions, 'handleNewRegistration');
        await handleNewRegistration({
          ...userData,
          uid: userCredential.user.uid
        });
      } catch (emailError) {
        console.error('Error sending registration emails:', emailError);
      }
      
      setUser({ ...userData, uid: userCredential.user.uid });
      // Redirect to Home if community member, otherwise Waitlist
      navigation.replace(isCommunityMember ? 'Home' : 'Waitlist');
    } catch (err) {
      console.error('Registration error:', err);
      let errorMessage = 'Registration failed';
      
      if (err.code === 'auth/email-already-in-use') errorMessage = 'Email already registered';
      else if (err.code === 'auth/invalid-email') errorMessage = 'Invalid email format';
      else if (err.code === 'auth/weak-password') errorMessage = 'Password is too weak';
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={[globalStyles.container, { minWidth: '100vw', backgroundColor: 'rgba(0,0,0,1)' }]}>
      <BackgroundVideo />
      {/* <ImageBackground
        source={require('../../assets/backgrounds/background.jpg')}
        style={globalStyles.backgroundImage}
        resizeMode="cover"
      > */}
        <ScrollView style={{ backgroundColor: 'rgba(0,0,0,1)' }}>
          <View style={[
            globalStyles.overlay, 
            {
              backgroundColor: 'rgba(0,0,0,1)', 
              paddingTop: -80
            }]}>
            <ScrollView 
              contentContainerStyle={[globalStyles.scrollContent, { paddingTop: 0 }]}
            showsVerticalScrollIndicator={false}
          >
            <View style={globalStyles.authContainer}>
              <View style={globalStyles.welcomeSection}>
                <Text style={globalStyles.welcomeText}>
                  Welcome to{'\n'}
                  <Text style={globalStyles.appName}>VENUE REPORT CARD</Text>
                </Text>
                <Image
                  source={require('../../assets/logos/logo.png')}
                  style={globalStyles.authLogo}
                />
              </View>

              {message ? (
                <Text style={[globalStyles.errorText, { backgroundColor: 'rgba(0, 111, 255, 0.2)', color: '#ffffff' }]}>
                  {message}
                </Text>
              ) : null}

              {isLogin ? (
                <View style={[globalStyles.formSection, loading && { opacity: 0.5 }]}>
                  <AuthInput 
                    label="EMAIL" 
                    value={formData.email} 
                    placeholder="Enter Email"
                    color="#97ff11"
                    onChangeText={(text) => setFormData({ ...formData, email: text })}
                    onSubmitEditing={handleLogin}
                  />
                  <AuthInput 
                    label="PASSWORD" 
                    placeholder="Enter Password"
                    color="#97ff11"
                    value={formData.password} 
                    onChangeText={(text) => setFormData({ ...formData, password: text })}
                    onSubmitEditing={handleLogin}
                    secureTextEntry 
                  />
                  
                </View>
              ) : (
                <View style={[globalStyles.formSection, loading && { opacity: 0.5 }]}>
                  <AuthInput
                    label="Email"
                    value={formData.email} 
                    placeholder="Enter Email" 
                    onChangeText={(text) => setFormData({ ...formData, email: text })}
                    onSubmitEditing={handleRegister}
                  />
                  <AuthInput
                    label="Password"
                    value={formData.password}
                    placeholder="Enter Password"
                    onChangeText={(text) => setFormData({ ...formData, password: text })}
                    onSubmitEditing={handleRegister}
                    secureTextEntry
                  />
                  <AuthInput
                    label="Confirm Password"
                    value={formData.confirmPassword}
                    placeholder="Re-Enter Password"
                    onChangeText={(text) => setFormData({ ...formData, confirmPassword: text })}
                    onSubmitEditing={handleRegister}
                    secureTextEntry
                  />
                  <AuthInput 
                    label="UserName" 
                    value={formData.username} 
                    placeholder="Enter Username" 
                    onChangeText={(text) => setFormData({ ...formData, username: text })}
                    onSubmitEditing={handleRegister}
                  />
                  <AuthInput
                    label="First Name" 
                    value={formData.firstName} 
                    placeholder="Enter First Name" 
                    onChangeText={(text) => setFormData({ ...formData, firstName: text })}
                    onSubmitEditing={handleRegister}
                  />
                  <AuthInput
                    label="Last Name"  
                    value={formData.lastName} 
                    placeholder="Enter Last Name" 
                    onChangeText={(text) => setFormData({ ...formData, lastName: text })}
                    onSubmitEditing={handleRegister}
                  />
                  <AuthInput
                    label="Your Role"
                    value={formData.userType}
                    onChangeText={(userType) => setFormData({ ...formData, userType })}
                    placeholder={animatedRolePlaceholder}
                    onSubmitEditing={handleRegister}
                    showPlaceholder
                  />
                  <AuthSelect
                    label="Account Type"
                    value={formData.accountType}
                    placeholder="Select account type"
                    options={ACCOUNT_TYPES}
                    onSubmitEditing={handleRegister}
                    onChange={(accountType) => setFormData({ ...formData, accountType })}
                  />
                </View>
              )}

              {error ? <Text style={globalStyles.errorText}>{error}</Text> : null}

              <View style={globalStyles.authActions}>
                <TouchableOpacity style={globalStyles.authLink} onPress={() => {
                  if (isLogin) {
                    handleLogin();
                  } else {
                  setIsLogin(true);
                  setError('');
                    setFormData({
                      username: '',
                    });
                  }
                }}>
                  <Text style={[globalStyles.authNavText, { color: isLogin ? '#97ff11' : 'white' }]}>LOGIN</Text>
                </TouchableOpacity>
                <TouchableOpacity style={globalStyles.authLink} onPress={() => {
                  navigation.navigate('Home');
                }}>
                  <Text style={globalStyles.authNavText}>CONTINUE AS GUEST</Text>
                </TouchableOpacity>
                <TouchableOpacity style={globalStyles.authLink} onPress={() => {
                  if (!isLogin) {
                    handleRegister();
                  } else {
                    setIsLogin(false);
                    setError('');
                    setFormData({
                      username: '',
                      firstName: '',
                      lastName: '',
                      email: '',
                      password: '',
                      confirmPassword: '',
                      userType: '',
                    });
                  }
                }}>
                  <Text style={[globalStyles.authNavText, { color: !isLogin ? '#97ff11' : 'white' }]}>REGISTER</Text>
                </TouchableOpacity>
                {/* <TouchableOpacity
                  style={globalStyles.authButton}
                  onPress={isLogin ? handleLogin : handleRegister}
                  disabled={loading}
                >
                  <Text style={globalStyles.buttonText}>
                    {loading ? 'Loading...' : isLogin ? 'Login' : 'Register Now'}
                  </Text>
                </TouchableOpacity>

                <View style={globalStyles.authToggle}>
                  <Text style={globalStyles.authToggleText}>
                    {isLogin ? "Don't have an account? " : "Already have an account? "}
                    <Text
                      style={globalStyles.authToggleLink}
                      onPress={() => {
                        setIsLogin(!isLogin);
                        setError('');
                        setFormData({
                          username: '',
                          firstName: '',
                          lastName: '',
                          email: '',
                          password: '',
                          confirmPassword: '',
                          role: '',
                        });
                      }}
                    >
                      {isLogin ? 'Register' : 'Sign in'}
                    </Text>
                  </Text>
                </View>

                <TouchableOpacity
                  style={[globalStyles.authButton, { backgroundColor: 'rgba(0,0,0,0.2)', marginTop: 10 }]}
                  onPress={() => navigation.navigate('Home')}
                >
                  <Text style={[globalStyles.buttonText, { color: '#97ff11' }]}>
                    Back to Home
                  </Text>
                </TouchableOpacity> */}
              </View>
            </View>
          </ScrollView>
          <StatusBar style="light" />
        </View>
        </ScrollView>
      {/* </ImageBackground> */}
    </View>
  );
} 